
import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ReactMarkdown from "react-markdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      type: "bot",
      text: "Hi! I'm the Scrubbles assistant. How can I help you today? Ask me about our services, pricing, hours, or anything else!",
    },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { type: "user", text: userMessage }]);
    setIsLoading(true);

    try {      
      const response = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: userMessage, session_id: sessionId }),
      });

      if (!response.body) {
        setMessages((prev) => [
        ...prev,
        {
          type: "bot",
          text: "I'm having trouble connecting. Please call us at 519-210-9274 for immediate assistance.",
        },
      ]);
      }

      const reader = response.body
        .pipeThrough(new TextDecoderStream()) // Convert Uint8Array → string
        .getReader();

      let buffer = '';
      let sentenceBuffer = ''; // Holds partial sentence text

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          // Flush any remaining sentence at the end
          if (sentenceBuffer.trim()) {
            setMessages(prev => [
              ...prev,
              { type: "bot", text: sentenceBuffer.trim() }
            ]);
          }
          break;
        }

        buffer += value;
        let lines = buffer.split('\n');
        buffer = lines.pop(); // Keep incomplete line

        for (let line of lines) {
          line = line.trim();
          if (!line) continue;

          if (line.startsWith('data:')) {
            try {
              const jsonData = JSON.parse(line.slice(5).trim());
              const text = jsonData.response || '';

              // Append to sentence buffer
              sentenceBuffer += text;

              // Split into sentences (keep punctuation)
              const sentences = sentenceBuffer.split(/(?<!\d)([.!?])\s+/);

              // Process complete sentences
              while (sentences.length > 2) {
                const sentence = (sentences.shift() + sentences.shift()).trim();
                if (sentence) {
                  setMessages(prev => [
                    ...prev,
                    { type: "bot", text: sentence }
                  ]);
                }
              }

              // Rebuild remaining partial sentence
              sentenceBuffer = sentences.join('');
            } catch (err) {
              console.error('Invalid JSON in event:', err.message);
            }
          }
        }
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          type: "bot",
          text: "I'm having trouble connecting. Please call us at 519-210-9274 for immediate assistance.",
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 bg-sky-600 hover:bg-sky-700 text-white rounded-full shadow-lg shadow-sky-600/30 flex items-center justify-center transition-all duration-300 hover:scale-110 ${
          isOpen ? "scale-0 opacity-0" : "scale-100 opacity-100"
        }`}
        data-testid="chatbot-open-btn"
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] chatbot-container bg-white rounded-2xl overflow-hidden flex flex-col"
          style={{ height: "500px", maxHeight: "calc(100vh - 120px)" }}
          data-testid="chatbot-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-sky-600 to-sky-700 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold">Scrubbles Assistant</p>
                <p className="text-xs text-sky-100">Always here to help</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
              data-testid="chatbot-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`chat-message ${msg.type} chat-bubble`}
              >
                <div
                  className={`px-4 py-3 rounded-2xl text-sm ${
                    msg.type === "user"
                      ? "bg-sky-600 text-white rounded-br-md"
                      : "bg-white text-slate-700 shadow-sm border border-slate-100 rounded-bl-md"
                  }`}
                >
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="chat-message bot">
                <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-md shadow-sm border border-slate-100 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-sky-600" />
                  <span className="text-sm text-slate-500">Typing...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 bg-white border-t border-slate-100">
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 rounded-full border-slate-200 focus:border-sky-500"
                data-testid="chatbot-input"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || isLoading}
                className="w-10 h-10 rounded-full bg-sky-600 hover:bg-sky-700 p-0"
                data-testid="chatbot-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-400 text-center mt-2">
              Or call us: 519-210-9274
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
